import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, X } from 'lucide-react';
import AccountSidebar from './AccountSidebar';

interface AccountLayoutProps {
  children: React.ReactNode;
  title?: string;
  subtitle?: string;
}

export function AccountLayout({ children, title, subtitle }: AccountLayoutProps) {
  const { t } = useTranslation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  return (
    // Frontoffice has a global fixed header (~64px). Offset account layout so sidebar/header aren't hidden behind it.
    // In practice it's a bit taller than 64px, so we use 80px (pt-20).
    <div className="min-h-screen bg-gray-50 pt-20">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        >
          <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
        </div>
      )}

      {/* Mobile sidebar */}
      <div className={`fixed top-20 bottom-0 left-0 z-50 w-72 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:hidden ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <AccountSidebar />
      </div>

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:top-20 lg:bottom-0 lg:flex lg:w-72 lg:flex-col">
        <AccountSidebar />
      </div>

      {/* Main content */}
      <div className="lg:pl-72">
        {/* Mobile header */}
        <div className="lg:hidden bg-white shadow-sm border-b border-gray-200 px-4 py-3">
          <div className="flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="p-2 rounded-md text-textSecondary hover:text-textPrimary hover:bg-gray-100 transition-colors"
            >
              <Menu className="h-6 w-6" />
            </button>
            {title && (
              <h1 className="text-lg font-semibold text-textPrimary">
                {title}
              </h1>
            )}
            <div className="w-10"></div> {/* Spacer for centering */}
          </div>
        </div>

        {/* Page header */}
        {(title || subtitle) && (
          <div className="bg-white shadow-sm border-b border-gray-200">
            <div className="px-4 sm:px-6 lg:px-8 py-6">
              <div className="flex items-center justify-between">
                <div>
                  {title && (
                    <h1 className="text-2xl font-display font-bold text-textPrimary">
                      {title}
                    </h1>
                  )}
                  {subtitle && (
                    <p className="text-textSecondary mt-1">
                      {subtitle}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1">
          {children}
        </main>
      </div>
    </div>
  );
}

export default AccountLayout;
